import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Modal,
  Backdrop,
  Fade,
  IconButton,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  Alert,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '../../ThemeContext';
import { GooglePlayIcon, AppleIcon } from './StoreIcons';
import {
  PLAY_STORE_URL,
  APP_STORE_URL,
  EDITOR_PLAY_STORE_URL,
  AI_PLAY_STORE_URL,
} from '../../constants/links';

// Local app logo assets
import appLogo from '../../assets/Filmhook_Apps_Logoonly.png';
import editorLogo from '../../assets/editor/editor_icon.png';
import aiLogo from '../../assets/filmhook_ai_icon.png';

// Icons
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CameraAltRoundedIcon from '@mui/icons-material/CameraAltRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

// Official WhatsApp Vector Icon
export const WhatsAppIcon = ({ size = 20, color = '#25D366' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill={color}
    style={{ flexShrink: 0 }}
  >
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2M12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 14.99 3.81 13.47 3.81 11.91C3.81 7.37 7.5 3.67 12.05 3.67M8.53 7.33C8.37 7.33 8.1 7.39 7.87 7.64C7.65 7.89 7 8.5 7 9.71C7 10.93 7.89 12.1 8.01 12.27C8.14 12.44 9.76 14.94 12.24 16C12.83 16.27 13.28 16.42 13.64 16.53C14.23 16.72 14.77 16.69 15.2 16.63C15.68 16.56 16.67 16.03 16.88 15.45C17.08 14.86 17.08 14.36 17.02 14.25C16.96 14.15 16.8 14.09 16.56 13.97C16.32 13.85 15.12 13.26 14.9 13.18C14.67 13.09 14.51 13.05 14.35 13.29C14.19 13.53 13.72 14.09 13.58 14.25C13.43 14.41 13.29 14.43 13.05 14.31C12.81 14.19 11.8 13.86 10.6 12.79C9.67 11.96 9.04 10.93 8.92 10.73C8.8 10.53 8.91 10.42 9.03 10.3C9.14 10.19 9.28 10.01 9.4 9.87C9.52 9.73 9.56 9.63 9.64 9.47C9.72 9.31 9.68 9.17 9.62 9.05C9.56 8.93 9.09 7.77 8.89 7.31C8.7 6.85 8.5 6.92 8.35 6.91C8.21 6.9 8.05 6.9 7.89 6.9L8.53 7.33Z" />
  </svg>
);

// Standard Popular Country Codes
export const COUNTRY_CODES = [
  { code: '+91', flag: '🇮🇳', label: '+91 (India)' },
  { code: '+1', flag: '🇺🇸', label: '+1 (US / Canada)' },
  { code: '+44', flag: '🇬🇧', label: '+44 (UK)' },
  { code: '+971', flag: '🇦🇪', label: '+971 (UAE)' },
  { code: '+61', flag: '🇦🇺', label: '+61 (Australia)' },
  { code: '+65', flag: '🇸🇬', label: '+65 (Singapore)' },
  { code: '+49', flag: '🇩🇪', label: '+49 (Germany)' },
  { code: '+33', flag: '🇫🇷', label: '+33 (France)' },
  { code: '+81', flag: '🇯🇵', label: '+81 (Japan)' },
];

// SVG Data URIs for the center of the QR code (excavated in the exact center)
export const GOOGLE_PLAY_QR_ICON =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48"><rect width="48" height="48" rx="12" fill="#ffffff"/><path fill="#4caf50" d="m10 8 16 16L10 40z"/><path fill="#ff9800" d="m26 24 6 6-22 10z"/><path fill="#f44336" d="M10 8 32 18l-6 6z"/><path fill="#2196f3" d="m32 18 8 4.2c1.3.7 1.3 2 0 2.7L32 29l-6-5z"/></svg>'
  );

export const APPLE_STORE_QR_ICON =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48"><rect width="48" height="48" rx="12" fill="#000000"/><path fill="#ffffff" d="M31 24.3c0-3.6 3-5.3 3.1-5.4-1.7-2.4-4.3-2.8-5.2-2.8-2.2-.2-4.4 1.3-5.5 1.3s-3-1.2-4.8-1.2c-2.4 0-4.8 1.4-6 3.7-2.6 4.5-.9 11.1 1.6 14.8 1.3 1.8 2.7 3.8 4.7 3.7 1.9-.1 2.6-1.2 4.9-1.2s2.9 1.2 4.9 1.2c2 0 3.3-1.8 4.6-3.6 1.4-2.1 2-4.1 2.1-4.2-.1-.1-4.4-1.6-4.4-6.3zM26.7 13.9c1-1.3 1.7-3 1.5-4.8-1.5.1-3.2 1-4.3 2.3-.9 1.1-1.7 2.9-1.5 4.6 1.7.2 3.3-.8 4.3-2.1z"/></svg>'
  );

// Exactly the 4 live app store editions
export const APP_TARGETS = [
  {
    id: 'filmhook-android',
    appKey: 'filmhook',
    name: 'Filmhook App',
    shortName: 'Filmhook',
    platform: 'Android',
    storeName: 'Google Play',
    badge: 'Flagship Mediaapp',
    url: PLAY_STORE_URL,
    icon: <GooglePlayIcon size={18} />,
    appLogoImg: appLogo,
    qrCenterIcon: GOOGLE_PLAY_QR_ICON,
    color: '#07a4f5',
    gradient: 'linear-gradient(135deg, #07a4f5 0%, #3b82f6 100%)',
    bg: 'rgba(7, 164, 245, 0.12)',
    border: 'rgba(7, 164, 245, 0.4)',
    description: 'Social networking & casting platform for cinema creators.',
    rating: '4.8 ★',
  },
  {
    id: 'filmhook-ios',
    appKey: 'filmhook',
    name: 'Filmhook App',
    shortName: 'Filmhook iOS',
    platform: 'iOS',
    storeName: 'App Store',
    badge: 'Apple iOS',
    url: APP_STORE_URL,
    icon: <AppleIcon size={18} />,
    appLogoImg: appLogo,
    qrCenterIcon: APPLE_STORE_QR_ICON,
    color: '#38bdf8',
    gradient: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
    bg: 'rgba(56, 189, 248, 0.12)',
    border: 'rgba(56, 189, 248, 0.4)',
    description: 'iPhone & iPad app for networking, auditions & locations.',
    rating: '4.9 ★',
  },
  {
    id: 'filmhook-editor',
    appKey: 'editor',
    name: 'Filmhook Editor',
    shortName: 'Editor',
    platform: 'Android',
    storeName: 'Google Play',
    badge: 'Video & Timeline Suite',
    url: EDITOR_PLAY_STORE_URL,
    icon: <GooglePlayIcon size={18} />,
    appLogoImg: editorLogo,
    qrCenterIcon: GOOGLE_PLAY_QR_ICON,
    color: '#a855f7',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)',
    bg: 'rgba(168, 85, 247, 0.12)',
    border: 'rgba(168, 85, 247, 0.4)',
    description: 'Multi-track 4K video editor with AI cutout & LUTs.',
    rating: '4.8 ★',
  },
  {
    id: 'filmhook-ai',
    appKey: 'ai',
    name: 'Filmhook AI',
    shortName: 'Filmhook AI',
    platform: 'Android',
    storeName: 'Google Play',
    badge: 'Generative Cinema AI',
    url: AI_PLAY_STORE_URL,
    icon: <GooglePlayIcon size={18} />,
    appLogoImg: aiLogo,
    qrCenterIcon: GOOGLE_PLAY_QR_ICON,
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
    bg: 'rgba(6, 182, 212, 0.12)',
    border: 'rgba(6, 182, 212, 0.4)',
    description: 'AI assistant for screenplays, storyboard generation & casting.',
    rating: '4.9 ★',
  },
];

/**
 * Realistic Animated Laser QR Scanner Viewport with Visible App & Store Icon Dock
 */
export const RealisticQRScannerBox = ({
  target,
  size = 140,
  onSelectTarget,
  showAllIcons = true,
  onOpenModal,
}) => { 
  const { isDarkMode } = useTheme();

  const currentTarget = target || APP_TARGETS[0];

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 2.2 },
        borderRadius: '24px',
        bgcolor: isDarkMode ? '#060c1c' : '#ffffff',
        border: isDarkMode
          ? '1.5px solid rgba(255, 255, 255, 0.12)'
          : '1.5px solid rgba(7, 164, 245, 0.18)',
        boxShadow: isDarkMode
          ? `0 20px 48px rgba(0, 0, 0, 0.7), 0 0 35px ${currentTarget.bg}`
          : `0 20px 48px rgba(15, 23, 42, 0.08), 0 0 25px ${currentTarget.bg}`,
        transition: 'all 0.35s ease',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 340,
      }}
    >
      {/* Top Banner: App Title & Store Badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          mb: 1.5,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              boxShadow: `0 2px 8px ${currentTarget.bg}`,
              border: `1px solid ${currentTarget.border}`,
            }}
          >
            <img
              src={currentTarget.appLogoImg}
              alt={currentTarget.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
          <Box sx={{ textAlign: 'left' }}>
            <Typography
              variant="subtitle2"
              fontWeight={800}
              sx={{
                fontSize: '0.86rem',
                color: isDarkMode ? '#ffffff' : '#0f172a',
                lineHeight: 1.2,
              }}
            >
              {currentTarget.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.68rem',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                display: 'block',
              }}
            >
              {currentTarget.platform} Edition
            </Typography>
          </Box>
        </Box>

        {/* Store Badge with Official Icon */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.1,
              py: 0.4,
              borderRadius: '12px',
              bgcolor: currentTarget.bg,
              border: `1px solid ${currentTarget.border}`,
            }}
          >
            <Box sx={{ display: 'flex', transform: 'scale(0.9)' }}>{currentTarget.icon}</Box>
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 800,
                color: currentTarget.color,
                letterSpacing: '0.02em',
              }}
            >
              {currentTarget.storeName}
            </Typography>
          </Box>

          {onOpenModal && (
            <Tooltip title="Expand Fullscreen Scanner" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenModal();
                }}
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  p: 0.5,
                  '&:hover': { color: currentTarget.color },
                }}
              >
                <FullscreenRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Realistic Scanner Viewfinder Viewport */}
      <Box
        onClick={onOpenModal}
        sx={{
          position: 'relative',
          width: size + 36,
          height: size + 36,
          borderRadius: '20px',
          bgcolor: '#ffffff',
          p: 1.8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: '0 10px 28px rgba(0, 0, 0, 0.12)',
          cursor: onOpenModal ? 'pointer' : 'default',
        }}
      >                  
        {/* Animated Laser Scanning Beam */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, transparent 0%, ${currentTarget.color} 50%, transparent 100%)`,
            boxShadow: `0 0 14px ${currentTarget.color}, 0 0 24px ${currentTarget.color}`,
            zIndex: 10,
            pointerEvents: 'none',
            animation: 'laserScan 2.4s ease-in-out infinite',
            '@keyframes laserScan': {
              '0%': { top: '5%' },
              '50%': { top: '93%' },
              '100%': { top: '5%' },
            },
          }}
        />

        {/* 4 Corner Viewfinder Brackets */}
        <Box
          sx={{
            position: 'absolute',
            top: 7,
            left: 7,
            width: 18,
            height: 18,
            borderTop: `3px solid ${currentTarget.color}`,
            borderLeft: `3px solid ${currentTarget.color}`,
            borderTopLeftRadius: '8px',
            zIndex: 5,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 7,
            right: 7,
            width: 18,
            height: 18,
            borderTop: `3px solid ${currentTarget.color}`,
            borderRight: `3px solid ${currentTarget.color}`,
            borderTopRightRadius: '8px',
            zIndex: 5,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 7,
            left: 7,
            width: 18,
            height: 18,
            borderBottom: `3px solid ${currentTarget.color}`,
            borderLeft: `3px solid ${currentTarget.color}`,
            borderBottomLeftRadius: '8px',
            zIndex: 5,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 7,
            right: 7,
            width: 18,
            height: 18,
            borderBottom: `3px solid ${currentTarget.color}`,
            borderRight: `3px solid ${currentTarget.color}`,
            borderBottomRightRadius: '8px',
            zIndex: 5,
          }}
        />

        {/* High-Contrast SVG QR Code with Store Logo Excavated in Exact Center */}
        <QRCodeSVG
          value={currentTarget.url}
          size={size}
          level="H"
          bgColor="#ffffff"
          fgColor="#0a0f1d"
          includeMargin={false}
          imageSettings={{
            src: currentTarget.qrCenterIcon,
            height: Math.round(size * 0.25),
            width: Math.round(size * 0.25),
            excavate: true,
          }}
        />
      </Box>

      {/* Camera Guidance & Verified Badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          mt: 1.4,
          px: 1.4,
          py: 0.4,
          borderRadius: '20px',
          bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          border: isDarkMode
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid #e2e8f0',
        }}
      >
        <CameraAltRoundedIcon sx={{ fontSize: 14, color: currentTarget.color }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            color: isDarkMode ? '#e2e8f0' : '#334155',
            fontSize: '0.7rem',
            letterSpacing: '0.02em',
          }}
        >
          Scan with phone camera
        </Typography>
        <VerifiedRoundedIcon sx={{ fontSize: 13, color: '#10b981', ml: 0.2 }} />
      </Box>

      {/* 4 App & Store Scanners Dock */}
      {showAllIcons && (
        <Box sx={{ width: '100%', mt: 1.6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.8,
              px: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.66rem',
                fontWeight: 800,
                color: isDarkMode ? '#94a3b8' : '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Select Scanner
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.62rem',
                fontWeight: 700,
                color: currentTarget.color,
              }}
            >
              ● {currentTarget.shortName} Active
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 0.8,
              bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
              p: 0.8,
              borderRadius: '16px',
              border: isDarkMode
                ? '1px solid rgba(255, 255, 255, 0.08)'
                : '1px solid #e2e8f0',
            }}
          >
            {APP_TARGETS.map((item) => {
              const isActive = item.id === currentTarget.id;
              return (
                <Tooltip
                  key={item.id}
                  title={`${item.name} (${item.storeName}) - Tap to switch scanner`}
                  arrow
                  placement="top"
                >
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSelectTarget) onSelectTarget(item);
                    }}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      py: 0.8,
                      px: 0.4,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      bgcolor: isActive
                        ? isDarkMode
                          ? 'rgba(255, 255, 255, 0.12)'
                          : '#ffffff'
                        : isDarkMode
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'transparent',
                      border: isActive
                        ? `2px solid ${item.color}`
                        : isDarkMode
                        ? '1px solid rgba(255, 255, 255, 0.06)'
                        : '1px solid transparent',
                      boxShadow: isActive
                        ? `0 4px 16px ${item.color}35`
                        : 'none',
                      transform: isActive ? 'scale(1.04)' : 'scale(1)',
                      transition: 'all 0.22s ease',
                      '&:hover': {
                        transform: 'scale(1.06)',
                        borderColor: item.color,
                        bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#ffffff',
                      },
                    }}
                  >
                    {/* Visual App Logo with mini store badge */}
                    <Box sx={{ position: 'relative', width: 28, height: 28, mb: 0.4 }}>
                      <img
                        src={item.appLogoImg}
                        alt={item.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: 7,
                          objectFit: 'cover',
                        }}
                      />

                      {/* Store Platform Badge Corner */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: -2,
                          right: -3,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: item.platform === 'iOS' ? '#000000' : '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                          border: '1.2px solid rgba(255,255,255,0.6)',
                        }}
                      >
                        {item.platform === 'iOS' ? (
                          <AppleIcon size={8} color="#ffffff" />
                        ) : (
                          <GooglePlayIcon size={8} />
                        )}
                      </Box>
                    </Box>

                    {/* App Short Title */}
                    <Typography
                      sx={{
                        fontSize: '0.62rem',
                        fontWeight: 800,
                        color: isActive ? item.color : isDarkMode ? '#e2e8f0' : '#1e293b',
                        textAlign: 'center',
                        lineHeight: 1.1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {item.shortName}
                    </Typography>

                    {/* Store Mini Label */}
                    <Typography
                      sx={{
                        fontSize: '0.52rem',
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                        lineHeight: 1,
                        mt: 0.3,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.platform === 'iOS' ? 'App Store' : 'Play Store'}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/**
 * Interactive QR Scanner Card for Deep Sections
 */
export const AppQRScannerCard = ({ initialTargetId = 'filmhook-android' }) => {
  const { isDarkMode } = useTheme();
  const [selectedIdx, setSelectedIdx] = useState(
    Math.max(0, APP_TARGETS.findIndex((t) => t.id === initialTargetId))
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState(0);

  const activeTarget = APP_TARGETS[selectedIdx] || APP_TARGETS[0];

  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: '28px',
        bgcolor: isDarkMode ? 'rgba(13, 20, 36, 0.85)' : '#ffffff',
        backdropFilter: 'blur(16px)',
        border: isDarkMode
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(7, 164, 245, 0.2)',
        boxShadow: isDarkMode
          ? '0 20px 50px rgba(0, 0, 0, 0.5)'
          : '0 20px 50px rgba(7, 164, 245, 0.1)',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        gap: { xs: 3, md: 4 },
        maxWidth: '850px',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background ambient light */}
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: '280px',
          height: '280px',
          background: `radial-gradient(circle, ${activeTarget.bg} 0%, transparent 70%)`,
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      {/* Left Column: Realistic Scanner Viewfinder */}
      <Box sx={{ flexShrink: 0, position: 'relative', zIndex: 1 }}>
        <RealisticQRScannerBox
          target={activeTarget}
          size={140}
          onSelectTarget={(target) => {
            const idx = APP_TARGETS.findIndex((t) => t.id === target.id);
            if (idx !== -1) setSelectedIdx(idx);
          }}
          showAllIcons={true}
          onOpenModal={() => setModalOpen(true)}
        />
      </Box>

      {/* Right Column: App Selectors & Direct Actions */}
      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          zIndex: 1,
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: { xs: 'center', md: 'flex-start' },
            mb: 1,
          }}
        >
          <Chip
            label={activeTarget.badge}
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '0.72rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              bgcolor: activeTarget.bg,
              color: activeTarget.color,
              border: `1px solid ${activeTarget.border}`,
            }}
          />
          <Chip
            label={`${activeTarget.rating} on ${activeTarget.storeName}`}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: '0.72rem',
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              color: isDarkMode ? '#e2e8f0' : '#334155',
            }}
          />
        </Box>

        <Typography
          variant="h4"
          fontWeight={900}
          sx={{
            fontSize: { xs: '1.6rem', md: '2rem' },
            color: isDarkMode ? '#ffffff' : '#0f172a',
            mb: 1,
          }}
        >
          {activeTarget.name}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? '#94a3b8' : '#64748b',
            mb: 2.5,
            lineHeight: 1.6,
          }}
        >
          {activeTarget.description}
        </Typography>

        {/* Action Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: { xs: 'center', md: 'flex-start' },
            flexWrap: 'wrap',
          }}
        >
          <Button
            component="a"
            href={activeTarget.url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={activeTarget.icon}
            endIcon={<OpenInNewRoundedIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              background: activeTarget.gradient,
              color: '#ffffff',
              fontWeight: 800,
              fontSize: '0.88rem',
              borderRadius: '20px',
              px: 3,
              py: 1.1,
              textTransform: 'none',
              boxShadow: `0 8px 24px ${activeTarget.bg}`,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 12px 28px ${activeTarget.bg}`,
              },
            }}
          >
            Open in {activeTarget.storeName}
          </Button>

          <Button
            onClick={() => {
              setModalInitialTab(0);
              setModalOpen(true);
            }}
            startIcon={<QrCodeScannerRoundedIcon />}
            sx={{
              borderRadius: '20px',
              fontWeight: 700,
              fontSize: '0.88rem',
              px: 2.2,
              py: 1.1,
              textTransform: 'none',
              border: isDarkMode
                ? '1.5px solid rgba(255,255,255,0.15)'
                : '1.5px solid #cbd5e1',
              color: isDarkMode ? '#ffffff' : '#1e293b',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
              },
            }}
          >
            Scan QR
          </Button>

          <Button
            onClick={() => {
              setModalInitialTab(1);
              setModalOpen(true);
            }}
            startIcon={<WhatsAppIcon size={17} />}
            sx={{
              borderRadius: '20px',
              fontWeight: 800,
              fontSize: '0.88rem',
              px: 2.4,
              py: 1.1,
              textTransform: 'none',
              bgcolor: isDarkMode ? 'rgba(37, 211, 102, 0.12)' : 'rgba(37, 211, 102, 0.08)',
              border: '1.5px solid rgba(37, 211, 102, 0.4)',
              color: isDarkMode ? '#4ade80' : '#15803d',
              boxShadow: '0 4px 14px rgba(37, 211, 102, 0.15)',
              '&:hover': {
                bgcolor: '#25D366',
                color: '#ffffff',
                borderColor: '#25D366',
                boxShadow: '0 6px 20px rgba(37, 211, 102, 0.35)',
              },
            }}
          >
            Send to Phone
          </Button>
        </Box>
      </Box>

      <AppQRScannerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultAppId={activeTarget.id}
        initialTab={modalInitialTab}
      />
    </Box>
  );
};

/**
 * Global QR Scanner & Send-to-Mobile Modal with Dual Modes:
 * 1. 📷 Animated Laser QR Scanner
 * 2. 📱 Direct WhatsApp & SMS Download Link Sender
 */
export const AppQRScannerModal = ({
  open,
  onClose,
  defaultAppId = 'filmhook-android',
  initialTab = 0,
}) => {
  const { isDarkMode } = useTheme();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [activeTab, setActiveTab] = useState(initialTab || 0);
  const [copied, setCopied] = useState(false);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [statusFeedback, setStatusFeedback] = useState(null);

  // Sync state on modal open
  React.useEffect(() => {
    if (open) {
      setActiveTab(initialTab || 0);
      setStatusFeedback(null);
      if (defaultAppId) {
        const idx = APP_TARGETS.findIndex(
          (t) =>
            t.id === defaultAppId ||
            t.id.startsWith(defaultAppId) ||
            t.name.toLowerCase().includes(defaultAppId.toLowerCase())
        );
        if (idx !== -1) setSelectedIdx(idx);
      }
    }
  }, [defaultAppId, open, initialTab]);

  const activeTarget = APP_TARGETS[selectedIdx] || APP_TARGETS[0];

  const handleCopyLink = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(activeTarget.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendWhatsApp = () => {
    const digitsOnly = phone.replace(/[^0-9]/g, '');
    if (!digitsOnly || digitsOnly.length < 8) {
      setStatusFeedback({
        type: 'error',
        message: 'Please enter a valid mobile number (at least 8-10 digits).',
      });
      return;
    }
    const cleanCode = countryCode.replace(/[^0-9]/g, '');
    const fullNumber = `${cleanCode}${digitsOnly}`;
    const message = `🎬 *Filmhook* — Official Mobile Download Link\n\nInstall *${activeTarget.name}* on ${activeTarget.storeName}:\n🔗 ${activeTarget.url}\n\nJoin 100K+ verified cinema creators, actors & directors on Filmhook! 🚀`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${fullNumber}&text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    setStatusFeedback({
      type: 'success',
      message: `✓ WhatsApp opened for ${countryCode} ${digitsOnly}! Tap Send in WhatsApp to deliver the link.`,
    });
  };

  const handleSendSMS = () => {
    const digitsOnly = phone.replace(/[^0-9]/g, '');
    if (!digitsOnly || digitsOnly.length < 8) {
      setStatusFeedback({
        type: 'error',
        message: 'Please enter a valid mobile number (at least 8-10 digits).',
      });
      return;
    }
    const cleanCode = countryCode.replace(/[^0-9]/g, '');
    const fullNumber = `+${cleanCode}${digitsOnly}`;
    const message = `🎬 Filmhook: Download ${activeTarget.name} on ${activeTarget.storeName}: ${activeTarget.url}`;

    // Trigger native SMS deep-link
    const smsUrl = `sms:${fullNumber}?body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;

    setStatusFeedback({
      type: 'success',
      message: `✓ SMS draft opened with your download link for ${fullNumber}!`,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        sx: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(5, 10, 20, 0.8)' },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: isDarkMode ? '#0d1527' : '#ffffff',
            color: isDarkMode ? '#ffffff' : '#0f172a',
            borderRadius: '28px',
            boxShadow: isDarkMode
              ? '0 25px 60px rgba(0,0,0,0.85), 0 0 40px rgba(7, 164, 245, 0.2)'
              : '0 25px 60px rgba(15,23,42,0.25)',
            border: isDarkMode
              ? '1.5px solid rgba(255, 255, 255, 0.12)'
              : '1.5px solid rgba(0, 0, 0, 0.08)',
            p: { xs: 2.5, sm: 3.5, md: 4 },
            maxWidth: 600,
            width: '92%',
            textAlign: 'center',
            maxHeight: '92vh',
            overflowY: 'auto',
          }}
        >
          {/* Header with App Branding and Close Button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '13px',
                  background: activeTarget.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  boxShadow: `0 6px 18px ${activeTarget.bg}`,
                }}
              >
                {activeTab === 0 ? (
                  <QrCodeScannerRoundedIcon sx={{ fontSize: 24 }} />
                ) : (
                  <PhoneIphoneRoundedIcon sx={{ fontSize: 24 }} />
                )}
              </Box>
              <Box sx={{ textAlign: 'left' }}>
                <Typography
                  variant="h6"
                  fontWeight={900}
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: 1.2 }}
                >
                  {activeTab === 0 ? 'Scan & Download App' : 'Send Link to Mobile'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontWeight: 600 }}
                >
                  {activeTab === 0
                    ? 'Instant Mobile Camera QR Scanner'
                    : 'Receive 1-tap download link on WhatsApp or SMS'}
                </Typography>
              </Box>
            </Box>

            <IconButton
              onClick={onClose}
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
                '&:hover': {
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
                },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          {/* Dual-Mode Selector Tabs */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <Tabs
              value={activeTab}
              onChange={(_, val) => {
                setActiveTab(val);
                setStatusFeedback(null);
              }}
              sx={{
                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
                p: 0.5,
                borderRadius: '20px',
                minHeight: 42,
                '& .MuiTabs-indicator': { display: 'none' },
              }}
            >
              <Tab
                icon={<QrCodeScannerRoundedIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Scan QR Code"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.78rem', sm: '0.84rem' },
                  textTransform: 'none',
                  minHeight: 36,
                  borderRadius: '16px',
                  px: { xs: 1.5, sm: 2.5 },
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  transition: 'all 0.25s ease',
                  ...(activeTab === 0 && {
                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.14)' : '#ffffff',
                    color: activeTarget.color + ' !important',
                    boxShadow: '0 3px 12px rgba(0,0,0,0.12)',
                  }),
                }}
              />
              <Tab
                icon={<WhatsAppIcon size={18} />}
                iconPosition="start"
                label="Send to Phone"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.78rem', sm: '0.84rem' },
                  textTransform: 'none',
                  minHeight: 36,
                  borderRadius: '16px',
                  px: { xs: 1.5, sm: 2.5 },
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  transition: 'all 0.25s ease',
                  ...(activeTab === 1 && {
                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.14)' : '#ffffff',
                    color: '#25D366 !important',
                    boxShadow: '0 3px 12px rgba(0,0,0,0.12)',
                  }),
                }}
              />
            </Tabs>
          </Box>

          {/* ══════════════════════════════════════
              TAB 0: SCAN QR CODE VIEW
              ══════════════════════════════════════ */}
          {activeTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <RealisticQRScannerBox
                  target={activeTarget}
                  size={150}
                  onSelectTarget={(target) => {
                    const idx = APP_TARGETS.findIndex((t) => t.id === target.id);
                    if (idx !== -1) setSelectedIdx(idx);
                  }}
                  showAllIcons={true}
                />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontSize: '0.86rem',
                  mb: 2.5,
                  px: 2,
                }}
              >
                {activeTarget.description}
              </Typography>
            </Box>
          )}

          {/* ══════════════════════════════════════
              TAB 1: DIRECT SEND TO MOBILE (WHATSAPP / SMS)
              ══════════════════════════════════════ */}
          {activeTab === 1 && (
            <Box sx={{ px: { xs: 0.5, sm: 1.5 }, mb: 2 }}>
              {/* Active Target Banner */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '16px',
                  bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                  border: `1.5px solid ${activeTarget.border}`,
                  mb: 2.5,
                }}
              >
                <img
                  src={activeTarget.appLogoImg}
                  alt={activeTarget.name}
                  style={{ width: 42, height: 42, borderRadius: 10, objectFit: 'cover' }}
                />
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: '0.92rem' }}>
                      {activeTarget.name}
                    </Typography>
                    <Chip
                      label={activeTarget.storeName}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.66rem',
                        bgcolor: activeTarget.bg,
                        color: activeTarget.color,
                        height: 20,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.74rem' }}
                  >
                    Direct official store link ready to dispatch
                  </Typography>
                </Box>
                <VerifiedRoundedIcon sx={{ color: '#10b981', fontSize: 20 }} />
              </Box>

              {/* Phone Input with Country Code Selector */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'left',
                    fontWeight: 800,
                    fontSize: '0.74rem',
                    color: isDarkMode ? '#e2e8f0' : '#334155',
                    mb: 0.8,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Enter Mobile Number
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    size="small"
                    sx={{
                      width: 140,
                      borderRadius: '14px',
                      bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                      color: isDarkMode ? '#fff' : '#0f172a',
                      fontWeight: 700,
                      fontSize: '0.84rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: isDarkMode
                          ? 'rgba(255, 255, 255, 0.15)'
                          : 'rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    {COUNTRY_CODES.map((item) => (
                      <MenuItem key={item.code} value={item.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <span>{item.flag}</span>
                          <span style={{ fontWeight: 800 }}>{item.code}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="98765 43210"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      if (statusFeedback) setStatusFeedback(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSendWhatsApp();
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIphoneRoundedIcon
                            sx={{
                              fontSize: 18,
                              color: isDarkMode ? '#94a3b8' : '#64748b',
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '14px',
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                        color: isDarkMode ? '#fff' : '#0f172a',
                        fontSize: '0.92rem',
                        fontWeight: 700,
                        '& fieldset': {
                          borderColor: isDarkMode
                            ? 'rgba(255, 255, 255, 0.15)'
                            : 'rgba(0, 0, 0, 0.15)',
                        },
                        '&:hover fieldset': {
                          borderColor: activeTarget.color,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: activeTarget.color,
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Status Alert Banner */}
              {statusFeedback && (
                <Alert
                  severity={statusFeedback.type}
                  sx={{
                    mb: 2,
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    textAlign: 'left',
                    fontWeight: 700,
                  }}
                  onClose={() => setStatusFeedback(null)}
                >
                  {statusFeedback.message}
                </Alert>
              )}

              {/* Dual Send Actions */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Button
                  onClick={handleSendWhatsApp}
                  startIcon={<WhatsAppIcon size={20} color="#ffffff" />}
                  sx={{
                    background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '0.88rem',
                    borderRadius: '18px',
                    py: 1.2,
                    textTransform: 'none',
                    boxShadow: '0 6px 20px rgba(37, 211, 102, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1ebd5a 0%, #0d7065 100%)',
                      boxShadow: '0 8px 24px rgba(37, 211, 102, 0.5)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Send via WhatsApp
                </Button>

                <Button
                  onClick={handleSendSMS}
                  startIcon={<SmsRoundedIcon sx={{ fontSize: 20 }} />}
                  sx={{
                    background: isDarkMode
                      ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                      : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                    color: '#ffffff',
                    fontWeight: 800,
                    fontSize: '0.88rem',
                    borderRadius: '18px',
                    py: 1.2,
                    textTransform: 'none',
                    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                      boxShadow: '0 8px 24px rgba(59, 130, 246, 0.45)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Send via SMS
                </Button>
              </Box>

              {/* Privacy Reassurance Note */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.8,
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontSize: '0.72rem',
                }}
              >
                <LockRoundedIcon sx={{ fontSize: 13, color: '#10b981' }} />
                <span>No spam. Your number is only used to send this official download link.</span>
              </Box>

              {/* 4 App Edition Dock in Send to Mobile View */}
              <Box sx={{ mt: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.8,
                    px: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.66rem',
                      fontWeight: 800,
                      color: isDarkMode ? '#94a3b8' : '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Select App to Send
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.64rem',
                      fontWeight: 700,
                      color: activeTarget.color,
                    }}
                  >
                    ● {activeTarget.shortName} Selected
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 0.8,
                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
                    p: 0.8,
                    borderRadius: '16px',
                    border: isDarkMode
                      ? '1px solid rgba(255, 255, 255, 0.08)'
                      : '1px solid #e2e8f0',
                  }}
                >
                  {APP_TARGETS.map((item) => {
                    const isActive = item.id === activeTarget.id;
                    return (
                      <Tooltip
                        key={item.id}
                        title={`Send ${item.name} (${item.storeName})`}
                        arrow
                        placement="top"
                      >
                        <Box
                          onClick={() => {
                            const idx = APP_TARGETS.findIndex((t) => t.id === item.id);
                            if (idx !== -1) setSelectedIdx(idx);
                          }}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 0.8,
                            px: 0.4,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            bgcolor: isActive
                              ? isDarkMode
                                ? 'rgba(255, 255, 255, 0.12)'
                                : '#ffffff'
                              : 'transparent',
                            border: isActive
                              ? `2px solid ${item.color}`
                              : '1px solid transparent',
                            boxShadow: isActive
                              ? `0 4px 16px ${item.color}35`
                              : 'none',
                            transform: isActive ? 'scale(1.04)' : 'scale(1)',
                            transition: 'all 0.22s ease',
                            '&:hover': {
                              transform: 'scale(1.06)',
                              borderColor: item.color,
                            },
                          }}
                        >
                          <img
                            src={item.appLogoImg}
                            alt={item.name}
                            style={{ width: 26, height: 26, borderRadius: 7, objectFit: 'cover' }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              color: isActive ? item.color : isDarkMode ? '#e2e8f0' : '#1e293b',
                              mt: 0.4,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}
                          >
                            {item.shortName}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}

          {/* Action Row */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              justifyContent: 'center',
              flexWrap: 'wrap',
              pt: 1,
              borderTop: isDarkMode
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid #f1f5f9',
            }}
          >
            <Button
              component="a"
              href={activeTarget.url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={activeTarget.icon}
              endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                background: activeTarget.gradient,
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.86rem',
                borderRadius: '20px',
                px: 3,
                py: 1,
                textTransform: 'none',
                boxShadow: `0 8px 20px ${activeTarget.bg}`,
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Open in {activeTarget.storeName}
            </Button>

            <Button
              onClick={handleCopyLink}
              startIcon={
                copied ? (
                  <CheckRoundedIcon sx={{ color: '#10b981' }} />
                ) : (
                  <ContentCopyRoundedIcon />
                )
              }
              sx={{
                borderRadius: '20px',
                fontWeight: 700,
                fontSize: '0.86rem',
                px: 2.5,
                py: 1,
                textTransform: 'none',
                border: isDarkMode
                  ? '1.5px solid rgba(255,255,255,0.15)'
                  : '1.5px solid #cbd5e1',
                color: isDarkMode ? '#ffffff' : '#1e293b',
                '&:hover': {
                  bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                },
              }}
            >
              {copied ? 'Link Copied!' : 'Copy Direct Link'}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default RealisticQRScannerBox;
